import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type SubscriptionTier = "free" | "basic" | "pro" | "premium" | "agent" | "all_access";

interface SubscriptionState {
  isLoading: boolean;
  subscribed: boolean;
  tier: SubscriptionTier;
  subscriptionEnd: string | null;
  analysisRemaining: number;
}

interface SubscriptionContextType extends SubscriptionState {
  checkSubscription: () => Promise<void>;
  openCheckout: (priceId: string) => Promise<void>;
  openCustomerPortal: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// Updated pricing with trial, annual discount, and agent bulk tier
export const SUBSCRIPTION_TIERS = {
  basic: {
    name: "Basic",
    price_id: "price_1TBda0EeOixj58u2PIBo69NH",
    price_id_annual: "price_1TBdbAEeOixj58u2EyembjNW",
    product_id: "prod_U9xVJ3KYIf938W",
    product_id_annual: "prod_U9xWwTZlqZzOWN",
    monthly_price: 19,
    annual_price: 182.40, // 20% discount
    analyses_limit: 10,
    endorsement_limit: 1,
    trial_days: 14,
    features: [
      "Core Contract Upload & Analysis",
      "Plain-English Summaries",
      "AI Confidence Scoring",
      "Risk Tier Classification",
      "10 Contract Analyses per Month",
      "1 Brand Deal Analysis",
      "Clause-Level Extraction",
    ],
  },
  pro: {
    name: "Pro",
    price_id: "price_1TBdcMEeOixj58u2wLyUf1ei",
    price_id_annual: "price_1TBdcdEeOixj58u2uCd7d10i",
    product_id: "prod_U9xXfgOdIGFAKF",
    product_id_annual: "prod_U9xXc6pGJ1Ew4y",
    monthly_price: 49,
    annual_price: 470.40,
    analyses_limit: -1, // Unlimited
    endorsement_limit: -1,
    trial_days: 14,
    features: [
      "Everything in Basic",
      "Unlimited Contract Analysis",
      "Unlimited Brand Deal Analysis",
      "Salary Benchmarking",
      "Take-Home Calculator",
      "Cost-of-Life Simulator",
      "Counter-Proposal Generator",
      "Contract Version Tracking",
      "Priority Email Support",
    ],
  },
  premium: {
    name: "Premium",
    price_id: "price_1TBdimEeOixj58u2fFn7yQuZ",
    price_id_annual: "price_1TBdk3EeOixj58u2yg7YjNSx",
    product_id: "prod_U9xekkMaNiVILL",
    product_id_annual: "prod_U9xfZglZyBLPCX",
    monthly_price: 99,
    annual_price: 950.40,
    analyses_limit: -1,
    endorsement_limit: -1,
    trial_days: 14,
    features: [
      "Everything in Pro",
      "Endorsement Shield Suite",
      "Negotiation Coach",
      "Contract Timeline View",
      "Career Dashboard & Projections",
      "AI Analysis Logs & Insights",
      "Expert Review Access",
      "Community Access",
      "Priority Support",
    ],
  },
  agent: {
    name: "Agent",
    price_id: "price_1TBdkXEeOixj58u20votnKiu",
    price_id_annual: "price_1TBdlfEeOixj58u2nVhMBsfS",
    product_id: "prod_U9xfxbzuhgKYQs",
    product_id_annual: "prod_U9xhcnxYiBTZ5A",
    monthly_price: 149,
    annual_price: 1430.40, // 20% discount
    analyses_limit: -1,
    endorsement_limit: -1,
    trial_days: 14,
    athlete_seats: 10,
    features: [
      "Everything in Premium",
      "Manage up to 10 Athletes",
      "Team Workspace",
      "Bulk Contract Analysis",
      "Client Portfolio Dashboard",
      "White-Label Reports",
      "Agent Marketplace Listing",
      "Dedicated Account Manager",
    ],
  },
} as const;

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SubscriptionState>({
    isLoading: true,
    subscribed: false,
    tier: "free",
    subscriptionEnd: null,
    analysisRemaining: 5, // Free tier: 5/month
  });

  const checkSubscription = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setState({
          isLoading: false,
          subscribed: false,
          tier: "free",
          subscriptionEnd: null,
          analysisRemaining: 5, // Free tier: 5/month
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke("check-subscription");
      
      if (error) {
        console.error("Error checking subscription:", error);
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      setState({
        isLoading: false,
        subscribed: data.subscribed,
        tier: data.tier as SubscriptionTier,
        subscriptionEnd: data.subscription_end,
        analysisRemaining: data.analysis_remaining,
      });
    } catch (error) {
      console.error("Failed to check subscription:", error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const openCheckout = useCallback(async (priceId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Failed to create checkout session:", error);
      throw error;
    }
  }, []);

  const openCustomerPortal = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Failed to open customer portal:", error);
      throw error;
    }
  }, []);

  useEffect(() => {
    checkSubscription();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkSubscription();
    });

    const interval = setInterval(checkSubscription, 60000);

    return () => {
      subscription.unsubscribe();
      clearInterval(interval);
    };
  }, [checkSubscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        ...state,
        checkSubscription,
        openCheckout,
        openCustomerPortal,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
}
